import React, { useState } from 'react';
import TextArea from 'components/atoms/TextArea';
import AttachButton from 'components/molecules/AttachButton';
import Button from 'components/atoms/Button';

interface ClassificationFormProps {
  onStartChat: (file?: File | null, message?: string, options?: { skipTariff?: boolean }) => void;
}

const ClassificationForm: React.FC<ClassificationFormProps> = ({ onStartChat }) => {
  const [inputText, setInputText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [skipTariff, setSkipTariff] = useState(true);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (inputText.trim() || attachedFiles.length > 0) {
      const fileToUpload = attachedFiles.length > 0 ? attachedFiles[0] : null;
      const message = inputText.trim() || undefined;
      onStartChat?.(fileToUpload, message, { skipTariff });
    }
  };

  const handleFileAttach = (files: File[]): void => {
    setAttachedFiles([...attachedFiles, ...files]);
  };

  const removeFile = (indexToRemove: number): void => {
    setAttachedFiles(attachedFiles.filter((_, index) => index !== indexToRemove));
  };

  return (
    <form className="mb-8" onSubmit={handleSubmit}>
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <TextArea
          placeholder="Describe your product or upload documents (invoice, catalog, spec sheet) to classify.

eg : What's the U.S. HTS code, duty rate, and PGA info for bicycle parts made in China? See attached product specifications and photograph"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={8}
          className="w-full border-none outline-none resize-none text-[14px] leading-6 text-gray-800 bg-transparent font-inherit p-0 mb-4"
        />
        
        {/* Show attached files */}
        {attachedFiles.length > 0 && (
          <div className="mb-4 space-y-2">
            {attachedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                <span className="text-[12px] text-gray-700">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AttachButton
              onFileSelect={handleFileAttach}
              acceptedTypes=".png,.pdf,.xml,.jpg,.jpeg"
              helperText="Support formats: PNG, PDF, XML"
              className="flex items-center gap-2"
            />
            <button
              type="button"
              onClick={() => setSkipTariff(!skipTariff)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] transition-colors cursor-pointer ${
                skipTariff
                  ? 'text-amber-700 hover:bg-amber-50'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
              title={skipTariff ? 'Tariff monitoring disabled — click to enable' : 'Click to skip tariff impact analysis'}
            >
              <span>{skipTariff ? 'Tariff off' : 'Tariff on'}</span>
            </button>
          </div>
          
          <Button
            type="submit"
            variant="teal"
            className="text-[12px] font-medium"
            disabled={!inputText.trim() && attachedFiles.length === 0}
          >
            Ask Classfi
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ClassificationForm;
